// MapOverlay.jsx
import React, { useEffect, useState } from "react";
import MapPane from "./MapPane";

export default function MapOverlay({
  isOpen,
  onClose,
  getLotsAsGeoJSON,  // () => Promise<FeatureCollection> -> SLTSnow_api.getLotsAsGeoJSON()
  selectedLotId,
  onSelectLot,       // scroll directory, etc.
  getLotOwnerDisplay, // (lotId) => string
}) {
  const [lotsGeoJson, setLotsGeoJson] = useState(null);
  const [hoverLotId, setHoverLotId] = useState(null);
  const [lotInfo, setLotInfo] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return;

    const updateViewportVars = () => {
      const vv = window.visualViewport;
      const height = vv?.height || window.innerHeight;
      const offsetTop = vv?.offsetTop || 0;
      document.documentElement.style.setProperty("--ios-vv-height", `${height}px`);
      document.documentElement.style.setProperty("--ios-vv-offset-top", `${offsetTop}px`);
    };

    updateViewportVars();
    window.addEventListener("resize", updateViewportVars);
    window.visualViewport?.addEventListener("resize", updateViewportVars);
    window.visualViewport?.addEventListener("scroll", updateViewportVars);

    return () => {
      window.removeEventListener("resize", updateViewportVars);
      window.visualViewport?.removeEventListener("resize", updateViewportVars);
      window.visualViewport?.removeEventListener("scroll", updateViewportVars);
      document.documentElement.style.removeProperty("--ios-vv-height");
      document.documentElement.style.removeProperty("--ios-vv-offset-top");
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    // Lock body scroll on iOS; prevents first-open touch offset on newer devices.
    // iOS Safari can mismatch visual vs layout viewport until scrolling is frozen.
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return;

    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prevBody = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    const prevHtmlOverflow = html.style.overflow;

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";

    return () => {
      body.style.position = prevBody.position;
      body.style.top = prevBody.top;
      body.style.width = prevBody.width;
      body.style.overflow = prevBody.overflow;
      html.style.overflow = prevHtmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
        // entering map: start clean so we don't show the panel automatically
        setLotInfo(null);
    } else {
        // exiting map: also clear (optional, but keeps behavior consistent)
        setLotInfo(null);
    }
    }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setError("");

    (async () => {
      try {
        const geo = await getLotsAsGeoJSON();
        if (!cancelled) setLotsGeoJson(geo);
      } catch (e) {
        if (!cancelled) setError(e?.message ?? "Failed to load map data");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, getLotsAsGeoJSON]);

  if (!isOpen) return null;

  return (
    <div className="mapOverlayBackdrop" role="dialog" aria-modal="true">
      <div className="mapOverlayPanel">
        <div className="mapOverlayHeader">
          <div className="mapOverlayTitle">Neighborhood Map</div>
          <button className="mapOverlayClose" onClick={onClose} aria-label="Close map">
            ✕
          </button>
        </div>

        {error ? <div className="mapOverlayError">{error}</div> : null}

        <div className="mapOverlayBody">
          <MapPane
            isOpen={isOpen}
            lotsGeoJson={lotsGeoJson}
            selectedLotId={selectedLotId}
            hoverLotId={hoverLotId}
            onHoverLot={setHoverLotId}
            onSelectLot={(lotId) => {
              console.log('[MapOverlay] onSelectLot called with:', lotId);
              onSelectLot?.(lotId); // your existing "scroll directory to lot"
              hoverLotId && setHoverLotId(null); // clear hover state
            }}
            onLotInfo={(info) => {
              console.log('[MapOverlay] onLotInfo called with:', info);
              setLotInfo(info);
            }}
          />

          {/* Bottom info panel */}
          {lotInfo ? (
            <div className="mapInfoPanel" role="status" aria-live="polite">
              {console.log('[MapOverlay] Rendering info panel with:', lotInfo)}
              <div className="mapInfoTopRow">
                <div className="mapInfoTitle">{lotInfo.lotLabel}</div>
                <button className="mapInfoDismiss" onClick={() => setLotInfo(null)} aria-label="Hide lot info">
                  ✕
                </button>
              </div>

              {lotInfo.ownersDisplay ? (
                <div className="mapInfoSub">{lotInfo.ownersDisplay}</div>
              ) : (
                <div className="mapInfoSub" style={{ opacity: 0.7 }}>
                  {getLotOwnerDisplay(lotInfo.lotId)}
                </div>
              )}

              <div className="mapInfoActions">
                <button
                  className="mapInfoActionBtn"
                  onClick={() => onSelectLot?.(lotInfo.lotId, true)}
                >
                  Jump to Directory
                </button>
              </div>
            </div>
          ) : (
            <div className="mapInfoHint">Tap a lot to see details</div>
          )}
        </div>
      </div>
    </div>
  );
}
